import { FC } from 'react'
import { TriangleAlert, Check, X } from 'lucide-react'
import { TooltipWrapper } from '@wellesley/ui'

type ProgressCircleProps = {
  progress: number
  uploadId: string
  onCancel?: (id: string) => void
}

type MediaStateProps = ProgressCircleProps & {
  errorMessage: string | null
  isSuccessVisible: boolean
  isProcessing: boolean
}

export const radius = 25
export const circumference = 2 * Math.PI * radius

export const getDashOffset = (progress: number) => {
  return circumference * (1 - progress / 100)
}

const ProgressCircle: FC<ProgressCircleProps> = ({
  progress,
  onCancel,
  uploadId,
}) => {
  return (
    <>
      <svg
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        width="52"
        height="52"
        viewBox="0 0 52 52"
        data-testid="upload-progress-svg"
      >
        <circle
          cx="26"
          cy="26"
          r={radius}
          fill="none"
          stroke="var(--white)"
          strokeWidth="2"
        />
        <circle
          cx="26"
          cy="26"
          r={radius}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          stroke="var(--blue-500)"
          style={{
            strokeDasharray: `${circumference} ${circumference}`,
            strokeDashoffset: getDashOffset(progress),
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%',
          }}
        />
      </svg>
      <button
        className="relative z-[1] cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          onCancel?.(uploadId)
        }}
        data-testid="upload-progress-cancel"
      >
        <X size={24} />
      </button>
    </>
  )
}

const SuccessCircle: FC = () => {
  return (
    <div
      className="flex items-center justify-center opacity-100 transition-opacity duration-300"
      data-testid="upload-progress-success"
    >
      <Check size={24} />
    </div>
  )
}

const ProcessingSpinner: FC = () => {
  return (
    <TooltipWrapper
      tooltipText="Media is processing"
      size="small"
      theme="dark"
      side="top"
      sideOffset={40}
    >
      <svg
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        width="52"
        height="52"
        viewBox="0 0 52 52"
        data-testid="upload-processing-svg"
      >
        <circle
          cx="26"
          cy="26"
          r={radius}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          stroke="var(--alpha-white-30)"
        />
        <circle
          cx="26"
          cy="26"
          r={radius}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          stroke="var(--white)"
          style={{
            strokeDasharray: `${circumference * 0.35} ${circumference}`,
            strokeDashoffset: 0,
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%',
          }}
        />
      </svg>
    </TooltipWrapper>
  )
}

const MediaStateContent: FC<MediaStateProps> = ({
  errorMessage,
  isSuccessVisible,
  isProcessing,
  ...props
}) => {
  switch (true) {
    case isProcessing:
      return <ProcessingSpinner />
    case isSuccessVisible:
      return <SuccessCircle />
    case errorMessage !== null:
      return (
        <TooltipWrapper
          tooltipText={errorMessage}
          size="small"
          theme="dark"
          side="top"
          sideOffset={20}
        >
          <TriangleAlert size={24} />
        </TooltipWrapper>
      )
    default:
      return <ProgressCircle {...props} />
  }
}

export default MediaStateContent
